import { Injectable } from '@angular/core'
import { NotifyService } from './notifications/notify.service'
import { TranslocoService } from "@ngneat/transloco"
import { UserService } from './user.service'
import { ApiService } from './api.service'
import { OrderStatus } from './enums'
import { FirestoreService2 } from './firestore.service2'
import { GlobalService } from './global.service'
import { clearFieldsStartedBy, use } from './utils'
import { TimestampsService } from './timestamp.service'
import { CartService } from './cart.service'
import { deepClonePreservingFieldValues } from './timestampUtils'

let env: any

@Injectable({ providedIn: 'root' })
export class OrdersService {
  type; nick; apiUrl; count: number; program: any
  private fss: FirestoreService2

  constructor(
    private notify: NotifyService,
    public g: GlobalService,
    private transloco: TranslocoService,
    private userService: UserService,
    private timestamps: TimestampsService
  ) {
    console.info(`## ${this.constructor.name}`)
    env = (window as any).y4wenvs.ENV
    this.apiUrl = this.g.get('apiUrl')
  }

  async placeOrder(order) {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const api: ApiService = use<ApiService>(ApiService)
    return this.fss.toPromise(api.post('transactions/placeOrder', order, true))
  }

  async updateOrder(order: any): Promise<{ success: boolean, order: any }> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    order = clearFieldsStartedBy(order, '_')
    delete order.cartLines

    order.orderPoints = 0
    order.orderQty = 0
    order.orderEuros = 0

    const { reasons, orderPointsChange } = this.processCartItems(order)

    if (order.purchaseOrderDate !== '') order.status = OrderStatus.ALL_ORDERED

    // Recalculate order status based on active items
    const activeItems = order.cart.filter(i => i.status !== OrderStatus.CANCELED && !i.isTransport)
    if (!activeItems.length) {
      order.status = OrderStatus.CANCELED
      order.deliveryDate = OrderStatus.CANCELED
    } else {
      // There were cancellations, recalculate status based on remaining items
      order.status = this.calcOrderStatus(activeItems)
    }

    order.contactus = `${location.origin}/${this.g.type}/my-account/contactus`
    order.orderDate = order.orderDate ? this.timestamps.toDate(order.orderDate)?.toISOString() || null : null

    if (reasons !== 0) {
      order.program = order.program || {}
      order.program.templateId = this.g.program.emails['orderChange']
      order.subject = 'Alteração da encomenda '
    }

    // Calculate totals excluding canceled items
    const activeItemsForTotals = order.cart.filter(i => i.status !== OrderStatus.CANCELED && !i.isTransport)
    order.orderEuros = activeItemsForTotals.reduce((acc, val) => acc + +val.amtEuros, 0)
    order.orderPoints = activeItemsForTotals.reduce((acc, val) => acc + +val.amtPoints, 0)
    order.orderQty = activeItemsForTotals.reduce((acc, val) => acc + +val.qty, 0)

    if (orderPointsChange !== 0) this.placeOrder(order)
    this.saveOrder(order)
    if (env.id === 'yes4eu' && reasons !== 0) this.sendConfirmationEmail(order)

    return { success: true, order }
  }

  private processCartItems(order: any): { reasons: number, orderPointsChange: number } {
    let reasons = 0, orderPointsChange = 0
    let orderCancelQty = 0, orderCancelAmt = 0, lastCancelDate: string = null

    for (const item of order.cart) {
      const isCanceled = item.reason && item.reason !== '---'

      if (isCanceled) {
        if (!item.cancelDate) item.cancelDate = new Date().toISOString()
        if (!item.cancelQty && item.qty > 0) {
          item.cancelQty = item.qty
          item.cancelAmt = item.qty * item.price
        }
        if (item.cancelQty) {
          orderCancelQty += item.cancelQty
          orderCancelAmt += item.cancelAmt || 0
        }
        lastCancelDate = item.cancelDate
        item.status = OrderStatus.CANCELED
        // Keep original qty for history - do not zero out
        reasons++
      }

      // Only sum points from non-canceled items
      if (!isCanceled) orderPointsChange += item.qty * item.points

    }

    if (reasons > 0) {
      order.cancelDate = lastCancelDate
      order.cancelQty = orderCancelQty
      order.cancelAmt = orderCancelAmt
    }

    return { reasons, orderPointsChange }
  }

  private calcOrderStatus(activeItems: any[]): string {
    if (activeItems.length === 0) return OrderStatus.CANCELED

    const statuses = new Set(activeItems.map(i => i.status))

    // If all have the same status
    if (statuses.size === 1) {
      const status = activeItems[0].status
      if (status === OrderStatus.ORDERED) return OrderStatus.ALL_ORDERED
      if (status === OrderStatus.INVOICED) return OrderStatus.INVOICED
      return status
    }

    // Mixed statuses
    if (statuses.has(OrderStatus.PLACED) && (statuses.has(OrderStatus.ORDERED) || statuses.has(OrderStatus.INVOICED))) {
      return OrderStatus.PARTIAL_ORDERED
    }
    if (statuses.has(OrderStatus.ORDERED) && statuses.has(OrderStatus.INVOICED)) {
      return OrderStatus.PARTIAL_INVOICED
    }

    // Fallback: keep the most "advanced" status
    if (statuses.has(OrderStatus.INVOICED)) return OrderStatus.PARTIAL_INVOICED
    if (statuses.has(OrderStatus.ORDERED)) return OrderStatus.PARTIAL_ORDERED
    return OrderStatus.PLACED
  }

  private saveOrder(order: any) {
    const cartSrv = use<CartService>(CartService)
    cartSrv.normalizeSalePurchaseDates(order)
    const data = deepClonePreservingFieldValues(order)
    const api: ApiService = use<ApiService>(ApiService)
    if (order.id) {
      this.fss.merge('transactions', order.id, data, { nick: order.nick }).then(() => {
        this.notify.update('Os seus dados foram gravados.', 'btn-success', 3000)
        this.userService.recalcBalance(order)
        if (order.segment?.id) api.updateSegmentBalance({ nick: order.nick, segmentId: order.segment.id })
      })
    } else {
      this.fss.merge('transactions', this.fss.createId, { ...data, active: true }, { nick: order.nick }).then(() => {
        this.userService.recalcBalance(order)
        if (order.segment?.id) api.updateSegmentBalance({ nick: order.nick, segmentId: order.segment.id })
      })
    }
  }

  async sendConfirmationEmail(order) {
    // console.log('### sent expedition email')
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const _order = { ...order, status: this.transloco.translate(order.status) }
    _order.cart = _order.cart.map(i => ({ ...i, status: this.transloco.translate(i.status) }))
    const api: ApiService = use<ApiService>(ApiService)
    try {
      const docs = await this.fss.toPromise(api.post('messaging/sendOrderConfirmation', this.g.targetOrderToBuyer(_order), true))
      this.notify.update('Os seus dados foram gravados. Email enviado', 'btn-success', 3000)
      return docs
    } catch (error) {
      throw error
    }
  }
}
